import { QueryClient } from '@tanstack/react-query';
import {
  BookmarkCollectionResponse,
  BookmarkResponse,
  BookmarkContentResponse,
  BookmarkAction,
  BookmarkActionsRequest,
  BookmarkSectionActionRequestBody,
  BookmarkSectionActionResponse,
  BookmarkSectionActionSection,
  BookmarkSectionActionTemplate,
  ProfileData,
  HighlightsResponse,
  UserSessionResponse,
} from '../types/api';

const providerQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export class APIError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'APIError';
    this.status = status;
  }
}

// Base "Internal API" (ie: ^/data/*) request function for data endpoints
async function fetchFromData<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = endpoint.startsWith('/') ? `/data${endpoint}` : `/data/${endpoint}`;

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `API error: ${response.status} ${response.statusText}`,
      response.status,
    );
  }

  return response.json();
}

// based on handlers available in DataHandlers.py
const client = {
  userSession: () => {
    return fetchFromData<UserSessionResponse>('/user_session');
  },

  bookmark: (bookmarkId: number | string) => {
    return fetchFromData<BookmarkResponse>(`/bookmarks/${bookmarkId}`);
  },

  bookmarkActions: (actions: BookmarkAction[]) => {
    const request: BookmarkActionsRequest = { actions };
    return fetchFromData(`/bookmarks/actions`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  bookmarkSectionAction: (
    section: BookmarkSectionActionSection,
    action: BookmarkSectionActionTemplate,
  ) => {
    const body: BookmarkSectionActionRequestBody = { section, action };
    return fetchFromData<BookmarkSectionActionResponse>(`/sections/actions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  bookmarkContent: (bookmarkId: number | string) => {
    return fetchFromData<BookmarkContentResponse>(`/bookmarks/${bookmarkId}/content`);
  },

  bookmarks: (params: {
    section_type: 'home' | 'liked' | 'archive' | 'folder' | 'tag' | 'videos';
    page?: number;
    folder_id?: string;
    tag?: string;
    include_user?: boolean;
    sort?: 'newest' | 'oldest';
  }) => {
    const searchParams = new URLSearchParams();

    // Add required section_type
    searchParams.append('section_type', params.section_type);

    // Add optional parameters
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.folder_id) searchParams.append('folder_id', params.folder_id);
    if (params.tag) searchParams.append('tag', params.tag);
    if (params.include_user) searchParams.append('include_user', 'true');
    if (params.sort) searchParams.append('sort', params.sort);

    return fetchFromData<BookmarkCollectionResponse>(
      `/bookmarks?${searchParams.toString()}`,
    );
  },

  createHighlight: (
    bookmarkId: number | string,
    params: {
      text: string;
      position?: number;
      note?: string;
    },
  ) => {
    const formData = new URLSearchParams();
    formData.append('text', params.text);
    if (params.position !== undefined)
      formData.append('position', params.position.toString());
    if (params.note) formData.append('note', params.note);

    return fetch(`/data/bookmark/${bookmarkId}/highlight`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },

  deleteHighlight: (highlightId: number | string, formKey?: string) => {
    const formData = new URLSearchParams();
    if (formKey) {
      formData.append('form_key', formKey);
    }

    return fetch(`/data/highlight/${highlightId}/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },

  keySentences: (bookmarkId: number | string, text: string) => {
    const formData = new URLSearchParams();
    formData.append('text', text);

    return fetch(`/data/bookmarks/${bookmarkId}/key_sentences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    });
  },

  progress: (bookmarkId: number | string, progress: number) => {
    const params = new URLSearchParams();
    params.append('progress', `${progress}`);
    params.append('ajax', '1');

    return fetch(`/data/bookmarks/${bookmarkId}/progress?${params.toString()}`, {
      method: 'GET',
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    });
  },

  updateHighlightNote: (highlightId: number | string, note: string, formKey: string) => {
    const formData = new URLSearchParams();
    formData.append('note', note);
    formData.append('form_key', formKey);

    return fetch(`/data/highlight/${highlightId}/update_note`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      // The server returns an empty response for successful updates
      // Check if there's any content to parse
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return response.json();
      } else {
        // Return a success object for empty responses
        return { success: true };
      }
    });
  },

  addBookmark: async (url: string) => {
    const response = await fetch('/data/bookmarks/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ url }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      }
      throw new Error(data.error || 'Failed to add bookmark');
    }

    return data.bookmark;
  },

  profile: async (userId: string, page = 1) => {
    const search = new URLSearchParams({ page: `${page}` });
    const response = await fetch(`/data/profile/${userId}?${search}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('User not found or profile is private');
      }
      throw new Error(`Failed to fetch profile: ${response.status}`);
    }
    const data = (await response.json()) as ProfileData;
    return data;
  },

  updateBookmark: (
    bookmarkId: number | string,
    params: {
      title: string;
      url: string;
      description?: string;
    },
  ) => {
    return fetchFromData(`/bookmarks/${bookmarkId}/edit`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  highlights: (
    params: {
      page?: number;
      include_user?: boolean;
    } = {},
  ) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.include_user) searchParams.append('include_user', 'true');

    return fetchFromData<HighlightsResponse>(`/notes?${searchParams.toString()}`);
  },
};

export { client, fetchFromData, providerQueryClient };
